import React, { useState, useEffect, useCallback } from 'react';

interface WatchlistEntry {
  id: string;
  name: string;
  passport_number: string;
  nationality: string;
  reason: string;
  risk_level: string;
  added_date: string;
  added_by: string;
  notes: string;
  matches_count: number;
}

const STORAGE_KEY = 'cns_watchlist_entries';

function getStoredEntries(): WatchlistEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch { /* ignore */ }
  // Local-only storage until a platform watchlist API is available — never seed fake names
  return [];
}

function saveEntries(entries: WatchlistEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

const emptyForm: Omit<WatchlistEntry, 'id' | 'added_date' | 'added_by' | 'matches_count'> = {
  name: '',
  passport_number: '',
  nationality: '',
  reason: '',
  risk_level: 'medium',
  notes: '',
};

const WatchlistPage: React.FC = () => {
  const [entries, setEntries] = useState<WatchlistEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [modalMode, setModalMode] = useState<'closed' | 'add' | 'edit'>('closed');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    setEntries(getStoredEntries());
  }, []);

  const persist = useCallback((next: WatchlistEntry[]) => {
    setEntries(next);
    saveEntries(next);
  }, []);

  // --- Actions ---
  const handleAdd = () => {
    if (!form.name.trim()) return;
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const newEntry: WatchlistEntry = {
      id: Date.now().toString(),
      ...form,
      added_date: new Date().toISOString().slice(0, 10),
      added_by: user?.name || user?.email || 'Agent CNS',
      matches_count: 0,
    };
    persist([newEntry, ...entries]);
    setForm(emptyForm);
    setModalMode('closed');
  };

  const handleEdit = () => {
    if (!editingId || !form.name.trim()) return;
    const next = entries.map(e =>
      e.id === editingId ? { ...e, ...form } : e
    );
    persist(next);
    setEditingId(null);
    setForm(emptyForm);
    setModalMode('closed');
  };

  const handleRemove = (id: string) => {
    persist(entries.filter(e => e.id !== id));
    setConfirmDeleteId(null);
  };

  const openEdit = (entry: WatchlistEntry) => {
    setEditingId(entry.id);
    setForm({
      name: entry.name,
      passport_number: entry.passport_number,
      nationality: entry.nationality,
      reason: entry.reason,
      risk_level: entry.risk_level,
      notes: entry.notes,
    });
    setModalMode('edit');
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalMode('add');
  };

  // --- Filtrage ---
  const filtered = entries.filter(e => {
    const matchSearch =
      e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.passport_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRisk = riskFilter === 'all' || e.risk_level === riskFilter;
    return matchSearch && matchRisk;
  });

  const riskColor = (level: string) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const riskLabel = (level: string) => {
    switch (level) {
      case 'critical': return 'Critique';
      case 'high': return 'Élevé';
      case 'medium': return 'Moyen';
      case 'low': return 'Faible';
      default: return level;
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🎯 Liste de Surveillance</h1>
          <p className="text-gray-600">Gestion des personnes sous surveillance</p>
        </div>

        {/* Recherche + Filtres */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="search-watchlist" className="block text-sm font-medium text-gray-700 mb-1">Rechercher</label>
              <input
                id="search-watchlist"
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Nom ou numéro de passeport..."
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label htmlFor="risk-filter" className="block text-sm font-medium text-gray-700 mb-1">Niveau de risque</label>
              <select
                id="risk-filter"
                value={riskFilter}
                onChange={e => setRiskFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="all">Tous</option>
                <option value="critical">Critique</option>
                <option value="high">Élevé</option>
                <option value="medium">Moyen</option>
                <option value="low">Faible</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={openAdd}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              + Ajouter à la liste
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-gray-900">{entries.length}</div>
            <div className="text-sm text-gray-600">Total entrées</div>
          </div>
          <div className="bg-red-50 rounded-lg shadow p-4 border border-red-200">
            <div className="text-2xl font-bold text-red-600">
              {entries.filter(e => e.risk_level === 'critical').length}
            </div>
            <div className="text-sm text-red-800">Critiques</div>
          </div>
          <div className="bg-orange-50 rounded-lg shadow p-4 border border-orange-200">
            <div className="text-2xl font-bold text-orange-600">
              {entries.filter(e => e.risk_level === 'high').length}
            </div>
            <div className="text-sm text-orange-800">Risque élevé</div>
          </div>
          <div className="bg-blue-50 rounded-lg shadow p-4 border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">
              {entries.reduce((s, e) => s + e.matches_count, 0)}
            </div>
            <div className="text-sm text-blue-800">Correspondances</div>
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Personne</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Passeport</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Raison</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risque</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Correspondances</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date d'ajout</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    Aucune entrée trouvée
                  </td>
                </tr>
              ) : (
                filtered.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{entry.name}</div>
                      <div className="text-sm text-gray-500">{entry.nationality}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.passport_number || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{entry.reason}</div>
                      {entry.notes && <div className="text-xs text-gray-500 mt-1">{entry.notes}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${riskColor(entry.risk_level)}`}>
                        {riskLabel(entry.risk_level)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {entry.matches_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(entry.added_date).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="text-xs text-gray-500">{entry.added_by}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openEdit(entry)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Modifier
                      </button>
                      {confirmDeleteId === entry.id ? (
                        <span className="inline-flex items-center gap-1">
                          <button
                            onClick={() => handleRemove(entry.id)}
                            className="text-red-700 font-semibold hover:underline"
                          >
                            Confirmer
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-gray-500 hover:underline ml-1"
                          >
                            Annuler
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(entry.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Retirer
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal Ajouter / Modifier */}
        {modalMode !== 'closed' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
              <h2 className="text-xl font-bold mb-4">
                {modalMode === 'add' ? 'Ajouter à la liste de surveillance' : 'Modifier l\'entrée'}
              </h2>
              <div className="space-y-3">
                <div>
                  <label htmlFor="form-name" className="block text-sm font-medium text-gray-700">Nom complet *</label>
                  <input
                    id="form-name"
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Nom et prénom"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="form-passport" className="block text-sm font-medium text-gray-700">N° Passeport</label>
                    <input
                      id="form-passport"
                      type="text"
                      value={form.passport_number}
                      onChange={e => setForm({ ...form, passport_number: e.target.value })}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="AB123456"
                    />
                  </div>
                  <div>
                    <label htmlFor="form-nationality" className="block text-sm font-medium text-gray-700">Nationalité</label>
                    <input
                      id="form-nationality"
                      type="text"
                      value={form.nationality}
                      onChange={e => setForm({ ...form, nationality: e.target.value })}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="RDC, France..."
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="form-reason" className="block text-sm font-medium text-gray-700">Raison *</label>
                  <input
                    id="form-reason"
                    type="text"
                    value={form.reason}
                    onChange={e => setForm({ ...form, reason: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Motif de surveillance"
                  />
                </div>
                <div>
                  <label htmlFor="form-risk" className="block text-sm font-medium text-gray-700">Niveau de risque</label>
                  <select
                    id="form-risk"
                    value={form.risk_level}
                    onChange={e => setForm({ ...form, risk_level: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="critical">Critique</option>
                    <option value="high">Élevé</option>
                    <option value="medium">Moyen</option>
                    <option value="low">Faible</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="form-notes" className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    id="form-notes"
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={2}
                    placeholder="Informations complémentaires..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => { setModalMode('closed'); setForm(emptyForm); setEditingId(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={modalMode === 'add' ? handleAdd : handleEdit}
                  disabled={!form.name.trim() || !form.reason.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {modalMode === 'add' ? 'Ajouter' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistPage;
